import classnames from 'classnames'
import { Tooltip } from 'embed/components/Tooltip'
import TrackingPixel from 'embed/components/TrackingPixel'
import { PublicClassNames } from 'embed/constants'
import { isDev } from 'embed/constants/config'
import { CreditOffer } from 'embed/constants/models'
import { css } from 'emotion'
import { CreditCardFlow, CreditCardStep } from 'even-ts-static'
import * as React from 'react'
import { offerFlagTooltipContent } from '../../SpendingCalculator/SpendingProfile/disclosure'
import { OfferFlag } from '../OfferFlag'
import { CreditListItemDetails } from './components/CardDetails'
import { CreditListItemImageContainer } from './components/CardImage'
import { CreditListItemSpecs } from './components/CardSpecs'
import { useTheme } from 'embed/hooks'

const applicationStyles = require('embed/styles/application')
const commonStyles = require('embed/styles/results')
const creditStyles = require('../styles.scss')
const InfoSmall = require('../info-grey.svg')

export interface Props {
  applyNow?: boolean
  dispatchCreditCardOfferCta: (offer: CreditOffer) => void
  dispatchCreditCardOfferCtaSecondary?: (offer: CreditOffer) => void
  flow?: CreditCardFlow
  leadUuid?: string
  offer: CreditOffer
  toggleSpendingProfile?: (visible: boolean) => void
  rateTableUuid?: string
  rewardsEnabled?: boolean
  step?: CreditCardStep
}

export const CreditOfferListItem: React.FC<Props> = ({
  applyNow,
  dispatchCreditCardOfferCta,
  dispatchCreditCardOfferCtaSecondary,
  flow,
  leadUuid,
  offer: { details },
  offer,
  rateTableUuid,
  rewardsEnabled,
  step,
}) => {
  const [showFooter, setShowFooter] = React.useState(false)
  const { borderRadiusLarge, colorBorder } = useTheme()

  const onDetailsRendered = React.useCallback((numberOfDetails: number, numberOfDetailsShown: number) => {
    setShowFooter(numberOfDetails > numberOfDetailsShown)
  }, [])

  const creditListItemImageContainerProps = {
    applyNow,
    dispatchCreditCardOfferCta,
    dispatchCreditCardOfferCtaSecondary,
    flow,
    offer,
    step,
  }

  const offerFlagTooltip = (
    <Tooltip
      overlay={offerFlagTooltipContent}
      overlayStyle={{ maxWidth: '350px' }}
      placement="bottom"
      trigger={['hover', 'click']}
    >
      <div className={classnames(applicationStyles.infoIcon, creditStyles.infoIcon)}>
        <InfoSmall />
      </div>
    </Tooltip>
  )

  return (
    <li
      key={offer.uuid}
      className={classnames(
        PublicClassNames.Offer,
        commonStyles.offer,
        creditStyles.offer,
        {
          [commonStyles.preApproved]: details.preApproved || details.preQualified || details.preSelected,
        },
        css`
          border-color: ${colorBorder};
          border-radius: ${borderRadiusLarge};
        `,
      )}
      ref={(el: HTMLLIElement) => el}
      data-test-val={isDev ? offer.uuid : ''}
    >
      {rateTableUuid && leadUuid && (
        <TrackingPixel rateTableUuid={rateTableUuid} leadUuid={leadUuid} offerUuid={offer.uuid} />
      )}
      <OfferFlag tooltip={offerFlagTooltip} offer={offer} />
      <div className={classnames(creditStyles.offerDetailsContainer)}>
        <div className={classnames(creditStyles.creditCardInteractionsContainer)}>
          <CreditListItemImageContainer showFooter={showFooter} {...creditListItemImageContainerProps} />
        </div>
        <div className={classnames(creditStyles.creditCardDetailsContainer)}>
          <CreditListItemSpecs rewardsEnabled={!!rewardsEnabled} details={details} />
          <CreditListItemDetails onDetailsRendered={onDetailsRendered} details={details} />
        </div>
      </div>
    </li>
  )
}
